import { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/**
 * App-level error boundary. Without this, any render-time exception in a child
 * (e.g. a component reading a field the API no longer returns) unmounts the whole
 * React tree and the user sees a blank white page. This catches those, keeps the
 * rest of the app usable, and offers a recovery path.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Uncaught error in React tree:', error, info.componentStack);
    // Fire-and-forget crash report → ops email (server dedupes 1/signature/hour
    // with a global hourly cap, so an error loop can't flood anyone).
    try {
      const base = (import.meta.env.VITE_SUPABASE_URL || '').replace(/\/$/, '');
      const anon = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
      if (base && anon) {
        fetch(`${base}/functions/v1/report-error`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', apikey: anon, Authorization: `Bearer ${anon}` },
          body: JSON.stringify({
            source: 'react-error-boundary',
            message: String(error?.message || error).slice(0, 800),
            stack: String(info?.componentStack || error?.stack || '').slice(0, 2500),
            url: window.location.href,
          }),
        }).catch(() => {});
      }
    } catch { /* reporting must never throw */ }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
          <div className="max-w-md w-full text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Something went wrong</h1>
            <p className="text-gray-600 mb-6">
              This page hit an unexpected error. You can try again, or head back to the homepage.
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={this.handleReset}
                className="px-4 py-2 rounded-lg bg-pink-600 text-white font-medium hover:bg-pink-700 transition-colors"
              >
                Try again
              </button>
              <a
                href="/"
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-100 transition-colors"
              >
                Return to home
              </a>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
